/**
 * widget 运行时装配：按现成接口创建/销毁 bridge + session，并保证生命周期一一对应。
 *
 * 约束来源：CONTRACT.md 第四节 iframe 接入要求（来源策略、父窗口校验、
 * 握手版本协商）与需求 6/7 节（身份切换清理）。
 *
 * 职责边界：
 * - 这里**只做装配与生命周期**：身份、HTTP、实时连接都在 {@link WidgetSession}，
 *   协议与来源守卫都在 {@link WidgetBridge}；
 * - 会话层的 status 变化广播给宿主（widget.status），业务事件转成 widget.event；
 * - start() 幂等；stop() 后监听被摘掉、会话停止，且不再出站。
 */

import { createSupportApi } from '@support/client';
import { MemoryTokenStore } from '@support/platform';

import type { WidgetPostWindow, WidgetSelfWindow } from './bridge';
import { WidgetBridge } from './bridge';
import type { WidgetConfig, WidgetEnv } from './config';
import { resolveWidgetConfig } from './config';
import { WidgetSession } from './session';

/** widget 在宿主上报真实上下文之前使用的页面标识。 */
const INITIAL_PAGE_KEY = 'support-widget';

export interface WidgetRuntimeOptions {
  /** 环境变量形状（生产用 import.meta.env，测试注入普通对象）。 */
  env?: WidgetEnv;
  /** 测试可注入的 fetch；生产走全局 fetch。 */
  fetchImpl?: typeof fetch;
  /** iframe 的父窗口；顶层直接打开时传 null（只有精确来源模式可认领宿主）。 */
  parentWindow?: null | WidgetPostWindow;
  selfWindow: WidgetSelfWindow;
}

export interface WidgetRuntime {
  bridge: WidgetBridge;
  config: WidgetConfig;
  session: WidgetSession;
  start: () => void;
  stop: () => void;
}

export function createWidgetRuntime(options: WidgetRuntimeOptions): WidgetRuntime {
  const config = resolveWidgetConfig(options.env ?? import.meta.env);
  // support 令牌只存在于内存容器：绝不写 localStorage，也不进 URL
  const store = new MemoryTokenStore();
  const api = createSupportApi({
    ...(options.fetchImpl === undefined ? {} : { fetchImpl: options.fetchImpl }),
    getToken: () => store.token,
    httpBase: config.httpBase,
  });
  const session = new WidgetSession({
    api,
    context: { pageKey: INITIAL_PAGE_KEY },
    store,
    wsBase: config.wsBase,
  });
  const bridge = new WidgetBridge({
    handlers: session,
    hostOrigins: config.hostOrigins,
    ...(options.parentWindow === undefined ? {} : { parentWindow: options.parentWindow }),
    selfWindow: options.selfWindow,
    widgetVersion: config.widgetVersion,
  });

  let unsubscribes: (() => void)[] = [];
  let started = false;

  const start = (): void => {
    if (started) return;
    started = true;
    // 会话状态 -> 宿主广播（宿主据此更新悬浮窗状态/角标）；业务事件 -> widget.event
    unsubscribes.push(session.on('status', () => bridge.publishStatus()));
    unsubscribes.push(session.on('event', (payload) => bridge.publishEvent(payload)));
    bridge.start();
  };

  const stop = (): void => {
    started = false;
    for (const unsubscribe of unsubscribes.splice(0)) unsubscribe();
    bridge.stop();
    session.stop();
  };

  return { bridge, config, session, start, stop };
}
